import { z } from 'my-zod'
import { makeValidator } from '../../internal/validator'
import { polarisRentalProductModel } from './polaris-rental-product-model'

const validateWhatIncludedOrToBring = (val: string): z.IssueData | undefined => {
  if (val.length > 1123) {
    return {
      code: 'custom',
      message: `Max 1123 characters`
    }
  }
  const formattedValue = val.split('\n')

  if (formattedValue.length > 15) {
    return {
      code: 'custom',
      message: `Max 15 lines`
    }
  }

  const hasOverLimitItems = formattedValue.some((item) => item.length > 280)

  if (hasOverLimitItems) {
    return {
      code: 'custom',
      message: `Ensure each line does not exceed 280 characters before inserting a carriage return (pressing Enter).`
    }
  }
}

export const polarisRentalProductUpdate = polarisRentalProductModel
  .omit({
    shortId: true,
    lifecycleStatus: true,
    duration: true,
    createdDate: true,
    lastModifiedDate: true,
    whatToBring: true,
    whatsIncluded: true
  })
  .augment({
    whatToBring: z
      .string()
      .superRefine((text, ctx) => {
        const issue = validateWhatIncludedOrToBring(text)
        if (issue) ctx.addIssue(issue)
      })
      .nullable(),
    whatsIncluded: z
      .string()
      .superRefine((text, ctx) => {
        const issue = validateWhatIncludedOrToBring(text)
        if (issue) ctx.addIssue(issue)
      })
      .nullable(),

    productImage: z
      .object({
        url: z.string().nullish(),
        alt: z.string().nullish(),
        caption: z.string().max(65).nullish()
      })
      .nullable(),
    landingHeroImage: z
      .object({
        url: z.string().nullish(),
        alt: z.string().nullish(),
        caption: z.string().max(65).nullish()
      })
      .nullable(),
    galleryImages: z
      .object({
        url: z.string().nullish(),
        alt: z.string().nullish(),
        caption: z.string().max(65).nullish()
      })
      .array()
      .max(10),
    versionId: z.number().optional()
  })

export type polarisRentalProductUpdate = z.infer<typeof polarisRentalProductUpdate>

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const validatePolarisRentalProductUpdate = makeValidator<polarisRentalProductUpdate>((product, error, warn) => {
  // If we don't have shortId, this is a create - and we want to require whatToBring on new catalog items.
  if (product.whatToBring) {
    const issue = validateWhatIncludedOrToBring(product.whatToBring)
    if (issue && issue.message) error(issue.message, ['whatToBring'])
  }
  if (product.whatsIncluded) {
    const issue = validateWhatIncludedOrToBring(product.whatsIncluded)
    if (issue && issue.message) error(issue.message, ['whatsIncluded'])
  }

  if (product.legacyBookingUrl && !product.legacyBookingUrl.startsWith('https://')) {
    error('Link must start with https://', ['legacyBookingUrl'])
  }
  if (product?.addressId) {
    if (!product.address?.addressLine1) {
      error('Required */', ['address.addressLine1'])
    }
    if (!product.address?.city) {
      error('Required */', ['address.city'])
    }
    if (!product.address?.postalCode) {
      error('Required */', ['address.postalCode'])
    }
    if (!product.address?.stateProvince) {
      error('Required */', ['address.stateProvince'])
    }
    if (!product.address?.country) {
      error('Required */', ['address.country'])
    }
  }
})
